import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $createParagraphNode,
  $createTextNode,
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  $isParagraphNode,
  COMMAND_PRIORITY_HIGH,
  KEY_ENTER_COMMAND,
  KEY_BACKSPACE_COMMAND,
} from 'lexical';
import { useEffect } from 'react';
import { $isQuoteNode } from '@lexical/rich-text';
import { $createWhatsAppQuoteNode, $isWhatsAppQuoteNode } from '../nodes';

/**
 * Plugin to handle Enter and Backspace key behavior inside quote nodes
 * 
 * WhatsApp uses separate blockquote elements for each line.
 * When Enter is pressed inside a quote, we need to:
 * 1. Split the current quote at the cursor position
 * 2. Create a new quote node for the text after the cursor
 * 3. Keep both as separate quote nodes (WhatsApp style)
 * 
 * When Backspace is pressed in an empty quote, we need to:
 * 1. Delete the empty quote immediately
 * 2. Move cursor to the previous element
 */
export function WhatsAppQuotePlugin(): null {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    // Handle Enter key in quotes - WhatsApp style (each line is a separate quote)
    const removeEnterListener = editor.registerCommand(
      KEY_ENTER_COMMAND,
      (event: KeyboardEvent | null) => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) {
          return false;
        }

        const anchorNode = selection.anchor.getNode();
        
        // Traverse up to find if we are inside a quote
        let currentNode = anchorNode;
        let quoteNode = null;
        
        while (currentNode) {
          if ($isQuoteNode(currentNode) || $isWhatsAppQuoteNode(currentNode)) {
            quoteNode = currentNode;
            break;
          }
          if (currentNode.getType() === 'root') break;
          const parent = currentNode.getParent();
          if (!parent) break;
          currentNode = parent;
        }

        if (!quoteNode) {
          return false; // Not in a quote, let default behavior handle it
        }

        // We're in a quote - handle Enter key
        event?.preventDefault();

        // Get the paragraph inside the quote
        const paragraph = quoteNode.getFirstChild();
        if (!paragraph || !$isParagraphNode(paragraph)) {
          // No paragraph, create empty quote after
          const newQuote = $createWhatsAppQuoteNode();
          const newPara = $createParagraphNode();
          newQuote.append(newPara);
          quoteNode.insertAfter(newQuote);
          newPara.selectStart();
          return true;
        }

        // Check if the paragraph is empty
        if (paragraph.getChildrenSize() === 0) {
          // Empty quote - exit the quote and create a normal paragraph
          const newParagraph = $createParagraphNode();
          quoteNode.insertAfter(newParagraph);
          quoteNode.remove();
          newParagraph.select();
          return true;
        }

        // Not empty - need to split the text at cursor position
        const offset = selection.anchor.offset;
        const textAnchorNode = selection.anchor.getNode();
        
        // If we're in a text node, split it
        if ($isTextNode(textAnchorNode)) {
          const textContent = textAnchorNode.getTextContent();
          
          if (offset === 0) {
            // Cursor at beginning of text - move entire content to new quote
            const newQuote = $createWhatsAppQuoteNode();
            const newPara = $createParagraphNode();
            
            // Move all children to new paragraph
            const children = paragraph.getChildren();
            children.forEach(child => {
              child.remove();
              newPara.append(child);
            });
            
            newQuote.append(newPara);
            quoteNode.insertAfter(newQuote);
            newPara.selectStart();
            return true;
          }
          
          if (offset === textContent.length) {
            // Cursor at end of text - check if there are more siblings
            const nextSibling = textAnchorNode.getNextSibling();
            if (!nextSibling) {
              // At the very end, create empty quote
              const newQuote = $createWhatsAppQuoteNode();
              const newPara = $createParagraphNode();
              newQuote.append(newPara);
              quoteNode.insertAfter(newQuote);
              newPara.selectStart();
              return true;
            }
          }
          
          // Split the text node at cursor
          const [, afterText] = textAnchorNode.splitText(offset);
          
          // Create new quote with text after cursor and any following siblings
          const newQuote = $createWhatsAppQuoteNode();
          const newPara = $createParagraphNode();
          
          if (afterText) {
            afterText.remove();
            newPara.append(afterText);
          }
          
          // Move any remaining siblings
          let sibling = textAnchorNode.getNextSibling();
          while (sibling) {
            const next = sibling.getNextSibling();
            sibling.remove();
            newPara.append(sibling);
            sibling = next;
          }
          
          newQuote.append(newPara);
          quoteNode.insertAfter(newQuote);
          newPara.selectStart();
          return true;
        }

        // Default: create new empty quote
        const newQuote = $createWhatsAppQuoteNode();
        const newPara = $createParagraphNode();
        newQuote.append(newPara);
        quoteNode.insertAfter(newQuote);
        newPara.selectStart();
        return true;
      },
      COMMAND_PRIORITY_HIGH
    );
    
    // Register Backspace command handler
    const removeBackspaceListener = editor.registerCommand(
      KEY_BACKSPACE_COMMAND,
      (event: KeyboardEvent | null) => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) {
          return false;
        }

        const anchorNode = selection.anchor.getNode();
        
        // Traverse up to find if we are inside a quote
        let currentNode = anchorNode;
        let quoteNode = null;
        
        while (currentNode) {
          if ($isQuoteNode(currentNode)) {
            quoteNode = currentNode;
            break;
          }
          if (currentNode.getType() === 'root') break;
          const parent = currentNode.getParent();
          if (!parent) break;
          currentNode = parent;
        }

        if (!quoteNode) {
          return false; // Not in a quote
        }

        // Check if the quote is empty
        const paragraph = quoteNode.getFirstChild();
        if (!paragraph) {
          // Empty quote with no paragraph - delete it
          const prevSibling = quoteNode.getPreviousSibling();
          quoteNode.remove();
          
          if (prevSibling) {
            prevSibling.selectEnd();
          }
          
          event?.preventDefault();
          return true;
        }

        const textContent = paragraph.getTextContent();
        const offset = selection.anchor.offset;

        // If the quote is empty (no text) and cursor is at the beginning
        if (textContent === '' && offset === 0) {
          // Delete the empty quote
          const prevSibling = quoteNode.getPreviousSibling();
          quoteNode.remove();
          
          // Move cursor to the end of the previous element
          if (prevSibling) {
            prevSibling.selectEnd();
          }
          
          event?.preventDefault();
          return true;
        }

        // Let default behavior handle other cases
        return false;
      },
      COMMAND_PRIORITY_HIGH
    );

    // Cleanup both listeners
    return () => {
      removeEnterListener();
      removeBackspaceListener();
    };
  }, [editor]);

  return null;
}
